// 技术能力矩阵：10 个领域，从 public/data/*.md 的 H2 提取，加自评熟练度
// 熟练度来自能力雷达图自评分（0-100），后续可与雷达图数据同源

export type TechCategory = 'ai' | 'cloud' | 'security';
export type TechTier = 'core' | 'proficient' | 'familiar';

export interface TechDomain {
  key: string;
  name: string;
  category: TechCategory;
  tier: TechTier;
  score: number;              // 0-100 自评熟练度
  icon: string;               // font-awesome class
  summary: string;            // 一句话领域摘要
  highlights: string[];       // 3-6 个关键子模块（H2 提取，deep-focus 部分优先）
  keywords?: string[];        // 次要标签（可选）
  mdFile?: string;            // 对应 public/data/*.md 文件名（不含 .md）
}

// 熟练度 → 层级映射
export const scoreToTier = (score: number): TechTier => {
  if (score >= 85) return 'core';
  if (score >= 75) return 'proficient';
  return 'familiar';
};

// 分类中文标签
export const CATEGORY_LABEL: Record<TechCategory, string> = {
  ai: 'AI · Agent',
  cloud: '云计算 · 基础设施',
  security: '安全',
};

// 层级中文标签 + 描述
export const TIER_LABEL: Record<TechTier, { name: string; hint: string; range: string }> = {
  core: { name: '核心深耕', hint: '主要输出方向，可独立方案设计并落地交付', range: '85+' },
  proficient: { name: '熟练掌握', hint: '可承接方案设计与技术对齐', range: '75-84' },
  familiar: { name: '了解涉猎', hint: '概念熟悉，可协同工作', range: '<75' },
};

// 10 个技术领域：数据来源 = public/data/*.md 的 H2 章节 + 能力雷达自评
export const TECH_DOMAINS: TechDomain[] = [
  // ===== 核心深耕 (85+) =====
  {
    key: 'cloud',
    name: '云计算 · 虚拟化',
    category: 'cloud',
    tier: 'core',
    score: 90,
    icon: 'fa-cloud',
    summary: '虚拟化 / PaaS / 中间件 / 云平台架构 · 6 年一线方案设计经验',
    highlights: ['服务器虚拟化 (KVM/vSphere)', 'PaaS 层组件', '中间件 · MQ / 缓存 / 应用服务器', '场景方案', '架构对比'],
    keywords: ['超融合 HCI', '私有云', '云管平台', '中间件'],
    mdFile: '云计算相关',
  },
  {
    key: 'ai',
    name: 'AI 相关',
    category: 'ai',
    tier: 'core',
    score: 88,
    icon: 'fa-brain',
    summary: 'Agent 工程 · RAG · 大模型推理集群 · GPU 池化 · AI Coding 首单落地',
    highlights: ['基础技术 (Transformer)', '训练技术', '推理优化 · vLLM / Triton', 'Context 工程', 'AI 网关', 'GPU 集群 · 算力调度'],
    keywords: ['LangChain', 'RAG', 'vLLM', 'CUDA', '模型仓库', '产业生态'],
    mdFile: 'AI相关',
  },
  {
    key: 'desktop-cloud',
    name: '云桌面 · aDesk',
    category: 'cloud',
    tier: 'core',
    score: 92,
    icon: 'fa-desktop',
    summary: '金融 / 制造场景化桌面云方案 · 8W+ 点位交付经验',
    highlights: ['场景化方案', '厂家分析', '价值主张', '架构对比'],
    keywords: ['VDI', 'DaaS', '桌面即服务'],
    mdFile: '云桌面相关',
  },
  {
    key: 'network',
    name: '网络相关',
    category: 'cloud',
    tier: 'core',
    score: 85,
    icon: 'fa-network-wired',
    summary: 'SDN/NFV 与零信任网络架构',
    highlights: ['协议技术', '硬件设备', 'SD-WAN', '零信任 ZTNA', 'NFV', 'SDN'],
    keywords: ['BGP', 'VXLAN'],
    mdFile: '网络相关',
  },
  {
    key: 'storage',
    name: '存储',
    category: 'cloud',
    tier: 'core',
    score: 85,
    icon: 'fa-hard-drive',
    summary: '分布式存储 EDS · 块/文件/对象存储',
    highlights: ['存储类型 (Block/File/Object)', '核心技术', '场景方案'],
    keywords: ['分布式存储', 'iSCSI', 'NoF', 'FC'],
    mdFile: '存储相关',
  },

  // ===== 熟练掌握 (75-84) =====
  {
    key: 'cloud-native',
    name: '云原生',
    category: 'cloud',
    tier: 'proficient',
    score: 82,
    icon: 'fa-dharmachakra',
    summary: 'K8s 容器编排与微服务架构 · DevOps 全链路',
    highlights: ['容器技术', 'K8s 编排', '微服务架构', 'DevOps'],
    keywords: ['Istio', 'Prometheus', 'Argo CD'],
    mdFile: '云原生相关',
  },
  {
    key: 'security',
    name: '安全 · 零信任',
    category: 'security',
    tier: 'proficient',
    score: 78,
    icon: 'fa-shield-halved',
    summary: '零信任 aTrust / 沙箱 / 安全咨询规划',
    highlights: ['终端安全', '网络安全', '云平台安全', '安全运营', '数据安全', '容器安全'],
    keywords: ['EDR', 'SASE', '态势感知', '合规'],
    mdFile: '安全相关',
  },
  {
    key: 'bigdata',
    name: '大数据',
    category: 'cloud',
    tier: 'proficient',
    score: 76,
    icon: 'fa-database',
    summary: 'Hadoop 体系与数据处理全链路',
    highlights: ['Hadoop 架构', '数据处理流程', '数据采集', '数据预处理', '存储管理'],
    keywords: ['Spark', 'Flink', 'Hive'],
    mdFile: '大数据相关',
  },
  {
    key: 'database',
    name: '数据库',
    category: 'cloud',
    tier: 'proficient',
    score: 75,
    icon: 'fa-table-cells',
    summary: '关系型/非关系型数据库选型与迁移',
    highlights: ['数据库分类', '优化方案', '高可用', '迁移方案'],
    keywords: ['MySQL', 'PostgreSQL', 'Redis', 'MongoDB'],
    mdFile: '数据库相关',
  },
];
